"use client";

import type { PlainDate } from "@sa-nu/shared-utils/date";
import { createContext, useContext, useMemo, type ReactNode } from "react";

import type { AccommodationReservationProviderInput } from "../../../../__generated__/gql/graphql";

import type { SelectedAccommodationReservation } from "./reservation-calendar";

/**
 * 予約カレンダーのデフォルト挙動を上書きするための拡張点。
 *
 * 対象は 2 つ:
 * - availability cell タップ (`onCellSelected`): デフォルトは
 *   `CheckOutDateSelectionModal` を即開く。
 * - 部屋番号 (ヘッダーセル) タップ (`onAccommodationSelected`): デフォルトは
 *   部屋タイプ詳細 (slug 無しなら拠点詳細) ページを別タブで開く。
 *
 * Provider なし: 現行通りのデフォルト挙動。
 * Provider あり: 各ハンドラに委譲。上位 (例: 部屋詳細 Drawer) は continuation の
 * `onReservationDraftSelected` コールバックを受け取り、チェックアウト日選択後に呼ぶ。
 *
 * 既存の OnlyExit (チェックアウト日変更) 分岐は override より優先。
 *
 * Payload は識別子のみ (accommodationId / date / provider / minNight / maxNight)。
 * 詳細データが必要なら受け手 (Drawer 等) が `Query.accommodation(id, ...)` で fetch する。
 */

export type CellSelectionPayload = {
  /** タップした日付 = チェックイン候補日 */
  date: PlainDate;
  accommodationId: string;
  /** カレンダーの provider 情報。Drawer 側で availability fetch に使う */
  provider: AccommodationReservationProviderInput;
  /** チェックアウト日選択の下限。カレンダーの minNight を引き継ぐ */
  minNight: PlainDate;
  /** チェックアウト日選択の上限。カレンダーの maxNight を引き継ぐ */
  maxNight: PlainDate;
};

export type CellSelectionContinuation = {
  /**
   * チェックアウト日選択が完了したときに呼ぶコールバック。
   * accommodationFacilities は呼び出し元 (カレンダー) が補完するため、
   * Omit した型を受け取る。
   */
  onReservationDraftSelected: (
    selected: Omit<SelectedAccommodationReservation, "accommodationFacilities">,
  ) => void;
};

export type ReservationFlowOverride = {
  onCellSelected: (
    payload: CellSelectionPayload,
    continuation: CellSelectionContinuation,
  ) => void | Promise<void>;
  /**
   * 部屋番号 (ヘッダーセル) タップ時のデフォルト挙動 (部屋タイプ詳細ページへの別タブ遷移) を
   * 上書きする。Provider があればセル・部屋番号の両操作を丸ごと乗っ取る。
   */
  onAccommodationSelected: (accommodationId: string) => void;
};

const ReservationFlowOverrideContext =
  createContext<ReservationFlowOverride | null>(null);

export function ReservationFlowOverrideProvider({
  onCellSelected,
  onAccommodationSelected,
  children,
}: {
  onCellSelected: ReservationFlowOverride["onCellSelected"];
  onAccommodationSelected: ReservationFlowOverride["onAccommodationSelected"];
  children: ReactNode;
}) {
  const value = useMemo(
    () => ({ onCellSelected, onAccommodationSelected }),
    [onCellSelected, onAccommodationSelected],
  );
  return (
    <ReservationFlowOverrideContext.Provider value={value}>
      {children}
    </ReservationFlowOverrideContext.Provider>
  );
}

export function useReservationFlowOverride(): ReservationFlowOverride | null {
  return useContext(ReservationFlowOverrideContext);
}
